import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { ApiError, api, unwrap, useDefinition, useDefinitions, useInvalidateFleet, useMeta } from '@/api';
import { PresetNote, PresetPicker } from '@/components/Presets';
import { PreviewPanel } from '@/components/Preview';
import { type JsonSchema, SchemaForm } from '@/components/SchemaForm';
import { Button, Card, Field, Input, PageHeader, ProtocolBadge, Spinner, Switch } from '@/components/ui';
import { PROTOCOL_LABELS } from '@/lib/format';

export function InstanceNew() {
	const [search, setSearch] = useSearchParams();
	const defs = useDefinitions();
	const definitionId = search.get('definition') ?? '';
	const def = useDefinition(definitionId || undefined);
	const meta = useMeta();
	const navigate = useNavigate();
	const invalidate = useInvalidateFleet();

	const [name, setName] = useState('');
	const [slug, setSlug] = useState('');
	const [interval, setIntervalMs] = useState(1000);
	const [seed, setSeed] = useState('');
	const [tags, setTags] = useState('');
	const [autostart, setAutostart] = useState(true);
	const [params, setParams] = useState<unknown>();
	const [faults, setFaults] = useState<unknown>({});
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (def.data) {
			setParams(structuredClone(def.data.defaults));
			setName((n) => n || def.data.name);
		}
	}, [def.data]);

	const create = useMutation({
		mutationFn: () =>
			unwrap(
				api.POST('/api/v1/instances', {
					body: {
						name,
						slug: slug || undefined,
						definitionId,
						sampleIntervalMs: interval,
						seed: seed || undefined,
						params: params as Record<string, unknown>,
						faults: faults as Record<string, unknown>,
						tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
						autostart,
					},
				}),
			),
		onSuccess: (i) => {
			toast.success(`Created ${i.name}`);
			invalidate();
			navigate(`/instances/${i.id}`);
		},
		onError: (e) => {
			if (e instanceof ApiError) setErrors(e.fieldErrors());
			toast.error(e.message);
		},
	});

	if (!definitionId) {
		return (
			<>
				<PageHeader title="New sensor" subtitle="Pick what kind of sensor to simulate." />
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{defs.data?.map((d) => (
						<button
							key={d.id}
							onClick={() => setSearch({ definition: d.id })}
							className="flex flex-col items-start gap-1.5 rounded-lg border border-border bg-surface p-4 text-left transition hover:border-accent"
						>
							<span className="flex w-full items-start justify-between gap-2">
								<span className="font-medium">{d.name}</span>
								<ProtocolBadge protocol={d.expectedProtocol} />
							</span>
							<span className="line-clamp-2 text-sm text-fg-muted">{d.description}</span>
						</button>
					))}
				</div>
			</>
		);
	}
	if (!def.data || params === undefined) return <Spinner />;
	const d = def.data;
	const paramErrors = Object.fromEntries(Object.entries(errors).filter(([k]) => k.startsWith('/params')).map(([k, v]) => [k.slice('/params'.length), v]));
	const faultErrors = Object.fromEntries(Object.entries(errors).filter(([k]) => k.startsWith('/faults')).map(([k, v]) => [k.slice('/faults'.length), v]));

	return (
		<>
			<PageHeader
				title={`New sensor · ${d.name}`}
				subtitle={
					<>
						From <Link to={`/definitions/${d.id}`} className="font-mono hover:underline">{d.id}</Link> · exposed over{' '}
						{PROTOCOL_LABELS[d.expectedProtocol]} with an automatically assigned address
					</>
				}
				actions={
					<>
						<Button onClick={() => setSearch({})}>Change definition</Button>
						<Button variant="primary" disabled={create.isPending || !name} onClick={() => (setErrors({}), create.mutate())}>
							Create sensor
						</Button>
					</>
				}
			/>
			<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
				<div className="flex min-w-0 flex-col gap-4">
					<Card title="Identity">
						<div className="grid gap-3 sm:grid-cols-2">
							<Field label="Name" error={errors['/name']}>
								<Input value={name} onChange={(e) => setName(e.target.value)} />
							</Field>
							<Field label="Slug" error={errors['/slug']} hint="Used in topics, URLs and node ids. Derived from the name if empty.">
								<Input value={slug} placeholder="auto" onChange={(e) => setSlug(e.target.value)} />
							</Field>
							<Field label="Sample every (ms)" error={errors['/sampleIntervalMs']} hint={`Minimum ${meta.data?.limits.minIntervalMs ?? 100} ms`}>
								<Input type="number" value={interval} onChange={(e) => setIntervalMs(Number(e.target.value))} />
							</Field>
							<Field label="Seed" error={errors['/seed']} hint="Same seed, same series. Random if empty.">
								<Input value={seed} placeholder="random" onChange={(e) => setSeed(e.target.value.replace(/\D/g, ''))} />
							</Field>
							<Field label="Tags" hint="Comma separated">
								<Input value={tags} onChange={(e) => setTags(e.target.value)} />
							</Field>
							<div className="flex items-end pb-1.5">
								<Switch checked={autostart} onChange={setAutostart} label="Start immediately" />
							</div>
						</div>
					</Card>
					<Card title="Parameters" actions={<PresetPicker defaults={d.defaults} presets={d.presets} onPick={setParams} />}>
						<PresetNote params={params} presets={d.presets} />
						<SchemaForm schema={d.paramsSchema as JsonSchema} value={params} onChange={setParams} errors={paramErrors} />
					</Card>
					<Card title="Faults">
						{meta.data && <SchemaForm schema={meta.data.faultsSchema as JsonSchema} value={faults} onChange={setFaults} errors={faultErrors} />}
					</Card>
				</div>
				<div className="min-w-0 xl:sticky xl:top-6 xl:self-start">
					<Card>
						<PreviewPanel definitionId={d.id} params={params} faults={faults} seed={seed} />
					</Card>
				</div>
			</div>
		</>
	);
}
